import React from 'react';
import { IonPage, IonRouterOutlet } from '@ionic/react';
import { Route, Redirect } from 'react-router-dom';
import { useAppConfig } from '@/modules/config';
import TopicRegistration from './TopicRegistration';
import Topics from './Topics';
import CreateTopicGate from './CreateTopicGate';
import TopicCreateForType from './TopicCreateForType';
import ReadTopicGate from './ReadTopicGate';
import UpdateTopicGate from './UpdateTopicGate';
import ShareFlowGate from '@/modules/share/ShareFlowGate';
import EngagementMetricsPage from './EngagementMetricsPage';
import TopicChangesPage from './TopicChangesPage';

// A topic id for the `:id` routes: any single segment that is neither the bare
// `new` chooser path nor a `new-<lowercase>` create verb (see the create route
// below). Excluding the create-verb shape makes the id routes and the create route
// mutually exclusive, so a URL matches exactly one — IonRouterOutlet does NOT
// disambiguate overlapping matches by declaration order, so exclusivity (not
// ordering) is what keeps `/topics/new-deal` off the topic-view route.
//
// Real topic ids are 14-char base64url strings, so they can start with `new`/`new-`
// (e.g. `new-Xy012_abcd`) — those still route to the view because the excluded shape
// requires the chars after `new-` to be *all lowercase letters*, which a create verb
// is and a base64url id effectively never is. Written as a char-class alternation
// because path-to-regexp v1 (React Router v5) rejects lookaheads / nested groups in a
// param's custom pattern.
//
// No id starts with `:` either. IonRouterOutlet re-pairs its routes with the views
// it already holds by matching each route against a view's path *pattern* as if it
// were a URL, so an id that could start with `:` would match the create route's
// pattern (`/topics/:createType(new-[a-z]+)`). The topic view's route then renders
// in the create form's place, and a pushed create form first transitions in a
// hidden topic view before its own page.
const ID_PATH_EXCLUDING_CREATE =
  ':topicId(n|ne|n[^e][^/]*|ne[^w][^/]*|new[^-/][^/]*|new-[a-z]*[^a-z/][^/]*|[^n:][^/]*)';

const TopicRouter: React.FC = () => {
  const { features, isLoaded } = useAppConfig();
  return (
    <IonPage>
      <IonRouterOutlet>
        <Route exact path="/topics" component={Topics} />
        <Route exact path="/topics/new" component={CreateTopicGate} />
        {/*
          Type-specific create route (`/topics/new-deal`, …). The whole `new-<type>`
          segment is captured as `createType` (a mid-segment `new-:type` param does not
          match reliably in path-to-regexp v1); TopicCreateForType strips the `new-`.
          The `:id` routes below exclude this same `new-<lowercase>` shape, so the two
          are mutually exclusive and order-independent.
        */}
        <Route
          exact
          path="/topics/:createType(new-[a-z]+)"
          component={TopicCreateForType}
        />
        <Route
          exact
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}/register`}
          render={routeProps => {
            // The registration page is gated behind the topicRegistrationEnabled
            // feature flag; redirect to the topic view when disabled.
            //
            // Mount TopicRegistration even before the app config has loaded. On a
            // hard reload landing directly here, returning null until `isLoaded`
            // gives IonRouterOutlet no view to establish for this location, and
            // swapping null -> component once config arrives does not mount the
            // page (the outlet's view stack is driven by route transitions, which
            // a same-location render change is not). Rendering the component up
            // front lets it show its own loading spinner; we only redirect once
            // we positively know the flag is disabled.
            const { topicId } = routeProps.match.params as unknown as {
              topicId: string;
            };
            if (isLoaded && !features.topicRegistrationEnabled) {
              return <Redirect to={`/topics/${topicId}`} />;
            }
            return <TopicRegistration />;
          }}
        />
        <Route
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}/share`}
          component={ShareFlowGate}
        />
        <Route
          exact
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}`}
          component={ReadTopicGate}
        />
        <Route
          exact
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}/engagement-metrics`}
          component={EngagementMetricsPage}
        />
        <Route
          exact
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}/changes`}
          component={TopicChangesPage}
        />
        <Route
          exact
          path={`/topics/${ID_PATH_EXCLUDING_CREATE}/edit`}
          component={UpdateTopicGate}
        />
      </IonRouterOutlet>
    </IonPage>
  );
};

export default TopicRouter;
